// src/components/ColumnsConfigModal.jsx
import { useEffect, useLayoutEffect, useRef, useState } from "react";

function Chip({ children, onClick }) {
  const [hover, setHover] = useState(false);
  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        background: hover ? "#EAF2FF" : "#F3F6FF",
        border: "1px solid #E6EDFF",
        color: "#1e63ff",
        borderRadius: 999,
        padding: "4px 10px",
        fontSize: 12,
        cursor: "pointer",
      }}
    >
      {children}
    </button>
  );
}

// Opciones permitidas + iconos Bootstrap
const OPTIONS = [
  { key: "etiquetas",   label: "Etiquetas",   icon: "bi-tags" },
  { key: "usuarios",    label: "Usuarios",    icon: "bi-people" },
  { key: "comentarios", label: "Comentarios", icon: "bi-chat-dots" },
  { key: "archivos",    label: "Archivos",    icon: "bi-paperclip" },
];

export default function ColumnsConfigModal({
  isOpen,
  onClose,
  anchorRef,      // ref del botón ⚙️
  value,          // { etiquetas:bool, usuarios:bool, comentarios:bool, archivos:bool }
  onChange,
  title = "Show only",
}) {
  const cardRef = useRef(null);
  const [pos, setPos] = useState({ top: 0, left: 0, minWidth: 260 });
  const [hoverIdx, setHoverIdx] = useState(-1);

  // Posiciona el popover cerca del botón
  useLayoutEffect(() => {
    if (!isOpen) return;
    const a = anchorRef?.current;
    if (!a) return;
    const r = a.getBoundingClientRect();
    const gap = 8;
    const minW = Math.max(260, r.width + 24);
    let left = r.left;
    if (left + minW > window.innerWidth - 8) left = Math.max(8, window.innerWidth - minW - 8);
    setPos({ top: r.bottom + gap + window.scrollY, left: left + window.scrollX, minWidth: minW });
  }, [isOpen, anchorRef]);

  // Cierra con click afuera / ESC
  useEffect(() => {
    if (!isOpen) return;
    const click = (e) => {
      if (cardRef.current?.contains(e.target) || anchorRef?.current?.contains(e.target)) return;
      onClose?.();
    };
    const esc = (e) => e.key === "Escape" && onClose?.();
    document.addEventListener("mousedown", click);
    document.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("mousedown", click);
      document.removeEventListener("keydown", esc);
    };
  }, [isOpen, onClose, anchorRef]);

  if (!isOpen) return null;

  const onlyKeys = OPTIONS.map(o => o.key);
  const toggleAll = (checked) =>
    onChange?.(Object.fromEntries(onlyKeys.map((k) => [k, checked])));

  return (
    <>
      {/* overlay transparente para capturar clics */}
      <div
        style={{
          position: "fixed",
          inset: 0,
          background: "transparent",
          zIndex: 999,
        }}
      />

      <div
        ref={cardRef}
        role="dialog"
        aria-modal="true"
        style={{
          position: "absolute",
          zIndex: 1000,
          background: "#fff",
          border: "1px solid #E6EDFF",
          borderRadius: 12,
          boxShadow: "0 8px 24px rgba(30, 99, 255, .10)",
          padding: 10,
          top: pos.top,
          left: pos.left,
          minWidth: pos.minWidth,
        }}
      >
        <div
          style={{
            fontSize: 12,
            fontWeight: 700,
            color: "#6B7280",
            textTransform: "uppercase",
            letterSpacing: ".4px",
            padding: "6px 8px 10px",
          }}
        >
          {title}
        </div>

        <div style={{ display: "flex", gap: 8, padding: "0 8px 6px" }}>
          <Chip onClick={() => toggleAll(true)}>Selecionar todos</Chip>
          <Chip onClick={() => toggleAll(false)}>Limpiar</Chip>
        </div>

        <ul
          style={{
            listStyle: "none",
            margin: 0,
            padding: "6px 4px 4px",
            maxHeight: "60vh",
            overflow: "auto",
          }}
        >
          {OPTIONS.map(({ key, label, icon }, i) => (
            <li
              key={key}
              onMouseEnter={() => setHoverIdx(i)}
              onMouseLeave={() => setHoverIdx(-1)}
              onClick={() => onChange({ ...value, [key]: !value[key] })}
              style={{
                ...(hoverIdx === i ? { background: "#F8FAFF", borderRadius: 8 } : null),
              }}
            >
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "8px 8px",
                  cursor: "pointer",
                }}
              >
                <input
                  type="checkbox"
                  checked={!!value[key]}
                  onChange={(e) => onChange({ ...value, [key]: e.target.checked })}
                  style={{ width: 18, height: 18, accentColor: "#3B82F6", cursor: "pointer" }}
                />
                <i className={`bi ${icon}`} style={{ width: 18, height: 18, color: "#94A3B8" }} />
                <span style={{ fontSize: 14, color: "#111827" }}>{label}</span>
              </label>
            </li>
          ))}
        </ul>
      </div>
    </>
  );
}
